import { createTheme, ThemeOptions } from '@mui/material/styles';
import { zhCN } from '@mui/material/locale';


const breakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920,
  },
};


const typography = {
  fontFamily: [
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
  ].join(','),
  h1: {
    fontSize: '2rem',
    fontWeight: 700,
    lineHeight: 1.2,
    [`@media (max-width: ${breakpoints.values.sm}px)`]: {
      fontSize: '1.75rem',
    },
  },
  h2: {
    fontSize: '1.75rem',
    fontWeight: 700,
    lineHeight: 1.3,
    [`@media (max-width: ${breakpoints.values.sm}px)`]: {
      fontSize: '1.5rem',
    },
  },
  h3: {
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.4,
    [`@media (max-width: ${breakpoints.values.sm}px)`]: {
      fontSize: '1.25rem',
    },
  },
  h4: {
    fontSize: '1.25rem',
    fontWeight: 600,
    lineHeight: 1.4,
    [`@media (max-width: ${breakpoints.values.sm}px)`]: {
      fontSize: '1.125rem',
    },
  },
  h5: {
    fontSize: '1.125rem',
    fontWeight: 600,
    lineHeight: 1.5,
    [`@media (max-width: ${breakpoints.values.sm}px)`]: {
      fontSize: '1rem',
    },
  },
  h6: {
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.5,
    [`@media (max-width: ${breakpoints.values.sm}px)`]: {
      fontSize: '0.875rem',
    },
  },
  body1: {
    fontSize: '1rem',
    lineHeight: 1.5,
    [`@media (max-width: ${breakpoints.values.sm}px)`]: {
      fontSize: '0.875rem',
    },
  },
  body2: {
    fontSize: '0.875rem',
    lineHeight: 1.5,
    [`@media (max-width: ${breakpoints.values.sm}px)`]: {
      fontSize: '0.75rem',
    },
  },
  button: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '1rem',
    [`@media (max-width: ${breakpoints.values.sm}px)`]: {
      fontSize: '0.875rem',
    },
  },
};


const themeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: '#FF006E',
      light: '#FF4D9A',
      dark: '#E0005E',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#2196F3',
      light: '#64B5F6',
      dark: '#1976D2',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#F44336',
      light: '#EF5350',
      dark: '#D32F2F',
    },
    warning: {
      main: '#FF9800',
      light: '#FFB74D',
      dark: '#F57C00',
    },
    info: {
      main: '#2196F3',
      light: '#64B5F6',
      dark: '#1976D2',
    },
    success: {
      main: '#4CAF50',
      light: '#81C784',
      dark: '#388E3C',
    },
    background: {
      default: '#F8F9FA',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1A1A1A',
      secondary: '#6B7280',
    },
    grey: {
      50: '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
      900: '#111827',
    },
  },
  typography,
  breakpoints,
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          minHeight: 44, 
          fontSize: '1rem',
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
          [`@media (max-width: ${breakpoints.values.sm}px)`]: {
            padding: '8px 16px',
            minHeight: 40,
            fontSize: '0.875rem',
          },
        },
        contained: {
          boxShadow: '0 4px 14px rgba(255, 0, 110, 0.25)',
          '&:hover': {
            boxShadow: '0 6px 20px rgba(255, 0, 110, 0.35)',
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-root': {
            fontSize: '1rem',
            [`@media (max-width: ${breakpoints.values.sm}px)`]: {
              fontSize: '16px', 
            },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          padding: 12,
          minWidth: 48, 
          minHeight: 48, 
          touchAction: 'manipulation',
          WebkitTapHighlightColor: 'transparent',
          [`@media (max-width: ${breakpoints.values.sm}px)`]: {
            padding: 12, 
            minWidth: 48, 
            minHeight: 48,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1), 0 4px 10px rgba(0, 0, 0, 0.06)',
            transform: 'translateY(-2px)',
          },
          [`@media (max-width: ${breakpoints.values.sm}px)`]: {
            borderRadius: 12,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          [`@media (max-width: ${breakpoints.values.sm}px)`]: {
            borderRadius: 8,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          margin: 16,
          width: 'calc(100% - 32px)',
          maxWidth: 600,
          [`@media (max-width: ${breakpoints.values.sm}px)`]: {
            margin: 8,
            width: 'calc(100% - 16px)',
            maxHeight: 'calc(100% - 16px)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          height: 64,
          [`@media (max-width: ${breakpoints.values.sm}px)`]: {
            height: 56,
          },
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          minWidth: 56,
          minHeight: 56,
          [`@media (max-width: ${breakpoints.values.sm}px)`]: {
            minWidth: 48,
            minHeight: 48,
          },
        },
      },
    },
  },
};

export const theme = createTheme(themeOptions, zhCN);
export default theme;

